<script setup lang="ts">
import useEmblaCarousel from 'embla-carousel-vue'
import { onMounted, ref, useTemplateRef } from 'vue'
import type { CmsCustomComponentType } from '~/layers/cms/app/types/cmsTypes'
import AppButton2 from './AppButton2.vue'

const props = defineProps<{
  cmsDataPath: (string | number)[]
  dataLength: number
  data: CmsCustomComponentType[]
}>()

const OPTIONS = { dragFree: true, loop: false }
const [emblaRef, emblaApi] = useEmblaCarousel(OPTIONS)

const isUserDragging = ref(false)

const emblaBtnPrev = useTemplateRef<HTMLElement>('emblaBtnPrev')
const emblaBtnNext = useTemplateRef<HTMLElement>('emblaBtnNext')

onMounted(() => {
  if (!emblaApi.value) return

  emblaBtnNext.value?.addEventListener('click', () => {
    emblaApi.value?.scrollNext()
  })

  emblaBtnPrev.value?.addEventListener('click', () => {
    emblaApi.value?.scrollPrev()
  })

  emblaApi.value.on('pointerDown', () => {
    isUserDragging.value = true
  })

  emblaApi.value.on('pointerUp', () => {
    isUserDragging.value = false
  })
})

const isYoutube = (url: string) => /youtu\.?be/.test(url)

const toYoutubeEmbed = (url: string) => {
  const id = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]+)/)?.[1]
  return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=0`
}
</script>

<template>
  <div class="flex flex-col gap-7.5 min-h-5 min-w-5">
    <div class="embla">
      <div
        ref="emblaRef"
        class="overflow-hidden"
      >
        <div
          class="flex flex-row gap-8 select-none"
        >
          <div
            v-for="(item, index) in props.data"
            :key="JSON.stringify(item)"
            class="embla__slide basis-auto shrink-0 max-w-65 xl:max-w-150"
          >
            <div class="flex flex-col gap-7.5 w-full justify-between">
              <div class="flex flex-col gap-7.5">
                <div>
                  <NuxtImg
                    v-if="data[index]?.poster?.value && data[index]?.poster.value != '' && (!data[index]?.video?.value || data[index]?.video.value == '')"
                    :src="(data[index]?.poster?.value as string)"
                    alt="image"
                    width="600"
                    height="340"
                    format="webp"
                    loading="eager"
                    quality="100"
                    fetchpriority="high"
                    class="rounded-3xl w-65 xl:w-150 h-40 xl:h-85 object-cover"
                  />
                  <template v-if="data[index]?.video?.value && data[index]?.video?.value != ''">
                    <iframe
                      v-if="isYoutube(data[index]?.video?.value as string)"
                      :src="toYoutubeEmbed(data[index]?.video?.value as string)"
                      class="rounded-3xl w-65 xl:w-150 h-40 xl:h-85"
                      frameborder="0"
                      allow="autoplay; encrypted-media"
                      allowfullscreen
                    />
                    <video
                      v-else
                      :src="(data[index]?.video?.value as string)"
                      autoplay
                      muted
                      loop
                      preload="auto"
                      fetchpriority="high"
                      playsinline
                      :poster="(data[index]?.poster?.value as string)"
                      class="rounded-3xl w-65 xl:w-150 h-40 xl:h-85"
                    />
                  </template>
                </div>
                <div class="flex flex-col gap-5 w-fit">
                  <span class="text-Tekst text-H3">
                    <CmsText
                      :cms-data-path="[...props.cmsDataPath, index, 'text_1']"
                    />
                  </span>
                  <span class="text-Tekst text-menu">
                    <CmsText
                      :cms-data-path="[...props.cmsDataPath, index, 'text_2']"
                    />
                  </span>
                  <span class="text-Tekst text-H4">
                    <CmsText
                      :cms-data-path="[...props.cmsDataPath, index, 'text_3']"
                    />
                  </span>
                </div>
              </div>
              <CmsLink
                v-slot="{ link }"
                :cms-data-path="[...props.cmsDataPath, index, 'link']"
              >
                <AppButton2
                  :href="link?.href"
                  :target="link?.target"
                >
                  <span class="link-text">{{ link?.link_text ?? 'Lorem Ipsum' }}</span>
                </AppButton2>
              </CmsLink>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="dataLength && dataLength > 0"
      class="flex items-start"
    >
      <div class="flex flex-row ">
        <div
          ref="emblaBtnPrev"
          class="px-7.5 py-5 border border-r-0 border-Tekst rounded-l-full transition-all duration-200 group group-hover:border-Secondary cursor-pointer"
        >
          <i class="pi pi-arrow-left text-Tekst group-hover:text-Secondary text-2xl! transition-all duration-200" />
        </div>
        <div
          ref="emblaBtnNext"
          class="px-7.5 py-5 border border-l-0 border-Tekst rounded-r-full transition-all duration-200 group group-hover:border-Secondary cursor-pointer"
        >
          <i class="pi pi-arrow-right text-Tekst group-hover:text-Secondary text-2xl! transition-all duration-200" />
        </div>
      </div>
    </div>
  </div>
</template>
